/**
 * /invite — branded invitation redemption page on pradam.org.
 *
 * Redesigned around JIT user creation: the Zitadel user is NOT
 * created until the invitee picks an authentication method here.
 * That gets us out of the pre-provisioning state machine that was
 * causing every Continue-with-Google attempt to detour through
 * verify-email screens.
 *
 * Flow:
 *   1. Page mounts → POST token to /api/get-invite-context, gets
 *      back { email, role, idpIds, … }.
 *   2. User picks Google / Microsoft / Institution / Passkey.
 *   3. POST { token, method } to /api/start-invite-redemption →
 *      backend JIT-creates the Zitadel user, grants the role, and
 *      returns either an idpId (for OIDC handoff) or a passkeyUrl
 *      (for direct redirect to Zitadel's WebAuthn page).
 *   4. For IdP: signinRedirect with IDP_SCOPE → bounce through
 *      Google/MS → Zitadel auto-links by email → home.
 *   5. For passkey: window.location to passkeyUrl on auth.delosis.com
 *      → user does WebAuthn → returns to pradam.org authenticated.
 *
 * The page deliberately ships no IdP IDs in the static bundle — they
 * arrive over the wire only after the JWT has been validated.
 */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PublicHeader from "../components/public/PublicHeader";
import PublicFooter from "../components/public/PublicFooter";
import { useDualMsal } from "../contexts/MsalContext";
import { buildIdpScopeRequest } from "../conf/authConfig";

export default function InvitePage({ onLoginClick }) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { userManager, currentUser } = useDualMsal();

  const params = new URLSearchParams(search);
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [context, setContext] = useState(null);
  const [error, setError] = useState(null);

  // If already authenticated when landing here (e.g. they finished
  // Google sign-in and bounced back), forward to their home.
  useEffect(() => {
    if (currentUser) {
      const roles = currentUser.roles || [];
      if (roles.includes("PRADAM.Admin") || roles.includes("PRADAM.PI")) {
        navigate("/admin", { replace: true });
      } else {
        navigate("/participant", { replace: true });
      }
    }
  }, [currentUser, navigate]);

  // Fetch the invitation context on mount.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      setError("This invitation link is missing its token.");
      return;
    }
    (async () => {
      try {
        const res = await fetch("/api/get-invite-context", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(body?.message || "We couldn't validate this invitation.");
        } else {
          setContext(body);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const startRedemption = async (method) => {
    setError(null);
    setSubmitting(method);
    try {
      const res = await fetch("/api/start-invite-redemption", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, method }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(body?.message || "Could not start sign-in.");
        setSubmitting(null);
        return;
      }
      if (method === "passkey") {
        if (!body.passkeyUrl) {
          setError("Passkey setup unavailable — please try another method.");
          setSubmitting(null);
          return;
        }
        window.location.href = body.passkeyUrl;
        return;
      }
      // IdP method — kick off OIDC with the IDP_SCOPE for the returned IdP.
      // We deliberately do NOT pass login_hint (it would re-resolve the
      // just-created Zitadel user before the IdP runs and could trip
      // Zitadel into a state-completion check).
      //
      // Deferred-creation flow: `inviteState` (when the backend returns
      // one — newer code path) rides along in oidc-client-ts's user-state
      // arg. It comes back as `user.state` on signinRedirectCallback, where
      // MsalContext POSTs it to /api/complete-invite-redemption to grant
      // the role + mint the Cosmos user / Participants docs against
      // whichever Zitadel user the federation actually returned (not
      // against invitation.email — fixes the email-mismatch orphan
      // accounts). Backward-compatible: an older backend that doesn't
      // return inviteState still works via the original upfront-create
      // flow.
      const idpId = body.idpId;
      if (!idpId) {
        setError("Sign-in is misconfigured — please contact the study team.");
        setSubmitting(null);
        return;
      }
      const idpRequest = buildIdpScopeRequest(idpId);
      const redirectArgs = body.inviteState
        ? { ...idpRequest, state: { inviteState: body.inviteState } }
        : idpRequest;
      await userManager.signinRedirect(redirectArgs);
    } catch (e) {
      setError(e?.message || "Network error.");
      setSubmitting(null);
    }
  };

  const studyName = context?.studyName || "PRADAM";
  const showEntra = context?.idpIds?.entra;
  const showGoogle = context?.idpIds?.google;
  const showMicrosoft = context?.idpIds?.microsoft;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PublicHeader onLoginClick={onLoginClick} />

      <Box
        component="main"
        sx={{ maxWidth: 520, mx: "auto", px: 2, py: { xs: 4, md: 8 } }}
      >
        <Card variant="outlined">
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h4" gutterBottom>
              Welcome to {studyName}
            </Typography>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && error && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && context && (
              <>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  You've been invited to take part in {studyName} as{" "}
                  <strong>{context.email}</strong>. Choose how you'd like to
                  sign in:
                </Typography>

                <Stack spacing={1.5} sx={{ mt: 3 }}>
                  {showEntra && (
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<AccountBalanceIcon />}
                      onClick={() => startRedemption("entra")}
                      disabled={!!submitting}
                    >
                      {submitting === "entra"
                        ? "Redirecting…"
                        : "Continue with Institution login"}
                    </Button>
                  )}

                  {showGoogle && (
                    <Button
                      fullWidth
                      variant={showEntra ? "outlined" : "contained"}
                      size="large"
                      startIcon={<GoogleIcon />}
                      onClick={() => startRedemption("google")}
                      disabled={!!submitting}
                    >
                      {submitting === "google"
                        ? "Redirecting…"
                        : "Continue with Google"}
                    </Button>
                  )}

                  {showMicrosoft && (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      startIcon={<MicrosoftIcon />}
                      onClick={() => startRedemption("microsoft")}
                      disabled={!!submitting}
                    >
                      {submitting === "microsoft"
                        ? "Redirecting…"
                        : "Continue with Microsoft"}
                    </Button>
                  )}

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    startIcon={<FingerprintIcon />}
                    onClick={() => startRedemption("passkey")}
                    disabled={!!submitting}
                  >
                    {submitting === "passkey"
                      ? "Setting up…"
                      : "Continue with this device"}
                  </Button>
                </Stack>

                <Divider sx={{ my: 3 }} />
                <Typography variant="caption" color="text.secondary">
                  Your account is created the moment you choose a sign-in
                  method — not before. Trouble signing in? Reply to the
                  invitation email and the study team will help.
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      <PublicFooter />
    </Box>
  );
}
